import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { Button } from './ui/Button';
import { GlassCard } from './ui/GlassCard';

export type MissionCheckpoint = {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  extra?: string;
  subLabel?: string;
  requiresPhoto?: boolean;
};

export function MissionControlCarousel({
  steps,
  completedStepIds,
  busy,
  gearPickupBlocked,
  onComplete,
  onPhotoSelected,
}: {
  steps: MissionCheckpoint[];
  completedStepIds: string[];
  busy: boolean;
  gearPickupBlocked: boolean;
  onComplete: (stepId: string) => void;
  onPhotoSelected: (event: ChangeEvent<HTMLInputElement>) => void | Promise<void>;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const currentStepIndex = useMemo(
    () => steps.findIndex(step => !completedStepIds.includes(step.id)),
    [steps, completedStepIds],
  );
  const completedCount = steps.filter(step => completedStepIds.includes(step.id)).length;
  const isAllComplete = steps.length > 0 && currentStepIndex === -1;
  const initialIndex = currentStepIndex === -1 ? Math.max(steps.length - 1, 0) : currentStepIndex;
  const [viewedIndex, setViewedIndex] = useState(initialIndex);

  const showStep = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const safeIndex = Math.max(0, Math.min(index, Math.max(steps.length - 1, 0)));
    setViewedIndex(safeIndex);
    const card = scrollerRef.current?.children.item(safeIndex) as HTMLElement | null;
    card?.scrollIntoView?.({ behavior, block: 'nearest', inline: 'start' });
  };

  useEffect(() => {
    const nextIndex = currentStepIndex === -1 ? Math.max(steps.length - 1, 0) : currentStepIndex;
    setViewedIndex(nextIndex);
    const frame = window.requestAnimationFrame(() => showStep(nextIndex));
    return () => window.cancelAnimationFrame(frame);
    // Advancing mission progress is the only event that automatically changes the viewed card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, steps.length]);

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller || scroller.clientWidth === 0) return;
    setViewedIndex(Math.max(0, Math.min(Math.round(scroller.scrollLeft / scroller.clientWidth), steps.length - 1)));
  };

  return (
    <GlassCard className="relative overflow-hidden rounded-[28px] border-brand-neonBlue/50 p-4 shadow-[0_0_30px_rgba(0,243,255,0.15)] sm:rounded-[32px] sm:p-6">
      <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-brand-neonBlue/0 via-brand-neonBlue to-brand-neonBlue/0 opacity-50" />
      <section aria-labelledby="mission-control-heading">
        <div className="mb-4 flex items-start justify-between gap-3 border-b border-brand-neonBlue/20 pb-4">
          <div>
            <h3 id="mission-control-heading" className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-brand-neonBlue">
              <Zap size={14} className={isAllComplete ? '' : 'animate-pulse'} /> 2 — Active Mission Control
            </h3>
            <p className="mt-2 text-xs font-semibold text-slate-400" aria-live="polite">
              {completedCount} of {steps.length} steps completed
            </p>
          </div>
          {steps.length > 0 && <span className="shrink-0 rounded-full border border-white/10 bg-slate-950 px-3 py-1 text-[10px] font-bold text-slate-400">{viewedIndex + 1} / {steps.length}</span>}
        </div>

        {steps.length === 0 ? (
          <p role="status" className="text-sm text-amber-300">Operational role required. Please ask Admin to select your operational role for this shift. Your job details and previous progress remain available.</p>
        ) : (
          <>
            {isAllComplete && (
              <div role="status" className="mb-4 rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-center">
                <p className="font-bold uppercase tracking-widest text-green-400">Shift Completed</p>
                <p className="mt-1 text-xs text-slate-400">All required mission checkpoints are complete.</p>
              </div>
            )}
            <div className="mb-4 flex items-center gap-1.5" aria-label={`${completedCount} of ${steps.length} mission steps completed`}>
              {steps.map((step, index) => {
                const complete = completedStepIds.includes(step.id);
                const current = index === currentStepIndex;
                return <span key={step.id} className={`h-1.5 flex-1 rounded-full transition-colors ${complete ? 'bg-green-500' : current ? 'bg-brand-neonBlue' : 'bg-slate-700'}`} />;
              })}
            </div>

            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              aria-label="Mission checkpoints"
            >
              {steps.map((step, index) => {
                const complete = completedStepIds.includes(step.id);
                const current = index === currentStepIndex;
                const upcoming = !complete && !current;
                const Icon = step.icon;
                const confirmDisabled = busy || (step.id === 'GEAR_LOADED' && gearPickupBlocked);

                return (
                  <article
                    key={step.id}
                    data-mission-step={step.id}
                    aria-current={current ? 'step' : undefined}
                    className={`min-w-full snap-start rounded-2xl border p-5 transition-all duration-300 ${current ? 'border-brand-neonBlue bg-brand-neonBlue/10 shadow-neon-blue-sm' : complete ? 'border-green-500/30 bg-green-500/5' : 'border-white/5 bg-slate-900/60 opacity-60'}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border ${current ? 'border-brand-neonBlue bg-brand-neonBlue text-slate-950 shadow-neon' : complete ? 'border-green-500 bg-green-500 text-slate-950' : 'border-white/10 bg-slate-800 text-slate-500'}`}>
                        {complete ? <Check size={21} strokeWidth={3} /> : <Icon size={21} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-[10px] font-black uppercase tracking-widest ${current ? 'text-brand-neonBlue' : complete ? 'text-green-400' : 'text-slate-500'}`}>
                          {complete ? 'Completed' : current ? 'Next action' : 'Upcoming'}
                        </p>
                        <h4 className={`mt-1 text-lg font-bold ${current ? 'text-white' : complete ? 'text-green-300' : 'text-slate-400'}`}>{step.label}</h4>
                        {step.subLabel && <p className="mt-1 text-xs font-medium text-slate-400">{step.subLabel}</p>}
                      </div>
                    </div>

                    {current && step.extra && (
                      <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-3 text-xs font-medium text-slate-300">
                        <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-brand-neonBlue">Travel info</span>
                        {step.extra}
                      </div>
                    )}

                    {current && (
                      <Button
                        disabled={confirmDisabled}
                        onClick={() => step.requiresPhoto ? photoInputRef.current?.click() : onComplete(step.id)}
                        className={`crew-mission-confirm mt-5 w-full rounded-xl border-none px-5 py-4 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none ${busy ? 'bg-slate-700 text-slate-400' : 'bg-brand-neonBlue text-slate-950 shadow-[0_0_15px_rgba(0,243,255,0.4)]'}`}
                      >
                        {busy ? 'Uploading...' : step.requiresPhoto ? 'Upload Photos & Confirm' : 'Confirm Checkpoint'}
                      </Button>
                    )}
                    {current && step.id === 'GEAR_LOADED' && gearPickupBlocked && <p className="mt-3 text-sm text-slate-300">Tick every current equipment item before confirming pickup.</p>}
                    {upcoming && <p className="mt-4 text-xs text-slate-500">Complete the earlier checkpoints before this step becomes available.</p>}
                  </article>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <button type="button" onClick={() => showStep(viewedIndex - 1)} disabled={viewedIndex === 0} aria-label="Previous mission checkpoint" className="flex min-h-[44px] items-center rounded-xl border border-white/10 bg-slate-900 px-4 text-xs font-bold text-slate-300 disabled:opacity-30">
                <ChevronLeft size={17} className="mr-1" /> Previous
              </button>
              <p className="hidden text-center text-[10px] font-semibold text-slate-500 sm:block">Swipe or use the controls to review steps</p>
              <button type="button" onClick={() => showStep(viewedIndex + 1)} disabled={viewedIndex >= steps.length - 1} aria-label="Next mission checkpoint" className="flex min-h-[44px] items-center rounded-xl border border-white/10 bg-slate-900 px-4 text-xs font-bold text-slate-300 disabled:opacity-30">
                Next <ChevronRight size={17} className="ml-1" />
              </button>
            </div>
          </>
        )}

        <input ref={photoInputRef} type="file" id="mission-photo-upload" className="hidden" accept="image/*" capture="environment" onChange={onPhotoSelected} />
      </section>
    </GlassCard>
  );
}
